import { motion, useReducedMotion } from "framer-motion";

/**
 * WJ Core — the perpetual identity object on the right side of the Hero.
 *
 * Lifecycle: an infinite ~16s cycle that never resets to a blank state.
 *   ┌───────────────────────────────────────────────────────────────────────┐
 *   │  0.0s  baseline calm (WJ + rings + halo alive)                        │
 *   │  2.0s  IDEA     — seed pulse at centre, editorial word bleeds in      │
 *   │  3.4s  DESIGN   — orbital rings shimmer, geometry emphasised          │
 *   │  4.8s  BUILD    — construction connections strengthen                 │
 *   │  6.2s  DELIVER  — structure completes, energy arcs charge             │
 *   │  7.6s  LAUNCH   — peak: full aura bloom, holds slightly longer        │
 *   │ 10.0s  resolve  — smooth morph back into calm WJ Core                 │
 *   │ 12.0s  hold     — quiet breathing, subtle motion, aura visible        │
 *   │ 16.0s  repeat                                                         │
 *   └───────────────────────────────────────────────────────────────────────┘
 * The crowned WJ mark, wordmark, halo bloom and orbital rings are visible in
 * every frame — the cycle modulates the *environment around them*.
 *
 * Interaction:
 *   • Desktop → restrained mouse parallax on halo / rings (not on the WJ mark)
 *   • Mobile  → scroll-linked parallax on the same layers
 *   • Reduced-motion → renders a calm static WJ Core, no cycle, no drift
 */

const CYCLE = 16; // seconds — one full lifecycle
const t = (s) => s / CYCLE; // helper: seconds → normalised 0..1

export default function WJCore() {
  const reduce = useReducedMotion();

  // Common perpetual transition (used for elements whose opacity cycles).
  const loopTrans = (times) => ({
    times,
    duration: reduce ? 0 : CYCLE,
    repeat: reduce ? 0 : Infinity,
    ease: "easeInOut",
  });

  return (
    <div
      className="wj-core-root relative w-full aspect-square max-w-[540px] mx-auto select-none"
      aria-hidden="true"
    >
      {/* ─── Local ambient mist (blends with the hero-wide environment behind) ─── */}
      <div className="absolute inset-0 wj-drift-50">
        <div className="absolute -inset-6">
          <div className="absolute left-[6%] top-[18%] w-[55%] h-[55%] rounded-full bg-[#0E9D6E]/22 blur-[70px]" />
          <div className="absolute right-[4%] bottom-[10%] w-[55%] h-[55%] rounded-full bg-[#2B8AF5]/22 blur-[80px]" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full bg-[#0B4B6F]/22 blur-[90px]" />
        </div>
      </div>

      {/* Stage words are rendered in WJEnvironment so they can be
          intentionally art-directed across the whole hero. */}

      {/* ─── Rings + halo + arcs (parallaxed group) ─── */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 w-full h-full wj-drift-50"
      >
        <defs>
          <radialGradient id="wj-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0E9D6E" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#0B4B6F" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#050505" stopOpacity="0" />
          </radialGradient>
          {/* Deep blue "eye" fill inside the portal — gives the WJ real depth */}
          <radialGradient id="wj-portal-eye" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0f2440" stopOpacity="0.75" />
            <stop offset="60%" stopColor="#0B4B6F" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#050505" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="wj-blue" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2B8AF5" stopOpacity="0" />
            <stop offset="50%" stopColor="#2B8AF5" stopOpacity="1" />
            <stop offset="100%" stopColor="#8ecbff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="wj-emerald" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0E9D6E" stopOpacity="0" />
            <stop offset="50%" stopColor="#3fdca6" stopOpacity="1" />
            <stop offset="100%" stopColor="#0E9D6E" stopOpacity="0" />
          </linearGradient>
          <filter id="wj-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Deep-blue portal interior — static, gives the WJ real depth */}
        <circle
          cx="200" cy="200" r="140"
          fill="url(#wj-portal-eye)"
          opacity="0.7"
        />

        {/* Halo bloom — continuous CSS breath, no per-frame React updates */}
        <circle
          cx="200" cy="200" r="150"
          fill="url(#wj-halo)"
          className="wj-halo-breath"
        />

        {/* Rotating outer ring — CSS spin, clearly perceptible */}
        <g className="wj-orbit-core-outer">
          <circle
            cx="200" cy="200" r="172"
            fill="none" stroke="#B79961" strokeWidth="0.6"
            opacity="0.6"
          />
          <circle cx="200" cy="28" r="2.6" fill="#28B37A" opacity="0.9" />
          <circle cx="200" cy="372" r="2.6" fill="#7B4BCC" opacity="0.9" />
        </g>

        {/* Counter-rotating mid ring — CSS spin, different speed */}
        <g className="wj-orbit-core-mid">
          <circle
            cx="200" cy="200" r="150"
            fill="none" stroke="#B79961" strokeWidth="0.5"
            opacity="0.5"
          />
          <circle cx="50" cy="200" r="2.2" fill="#7B4BCC" opacity="0.85" />
        </g>

        {/* Static inner rings — no animation, cheap to render */}
        <circle cx="200" cy="200" r="128" fill="none" stroke="#B79961" strokeWidth="0.4" opacity="0.35" />
        <circle cx="200" cy="200" r="108" fill="none" stroke="#B79961" strokeWidth="0.4" opacity="0.25" />
        <circle cx="200" cy="200" r="92"  fill="none" stroke="#B79961" strokeWidth="0.4" opacity="0.18" />

        {/* Emerald energy arc — charges during BUILD/DELIVER, peaks at LAUNCH */}
        <motion.path
          d="M 60 240 A 145 145 0 0 1 60 160"
          fill="none" stroke="url(#wj-emerald)" strokeWidth="6.5"
          strokeLinecap="round" filter="url(#wj-glow)"
          initial={{ opacity: 0 }}
          animate={{
            opacity: reduce ? 0.85 : [0, 0.1, 0.35, 0.6, 0.95, 0.4, 0],
          }}
          transition={loopTrans([0, t(4.5), t(5.5), t(6.5), t(8.4), t(11), 1])}
        />
        {/* Electric blue arc — sweeps in strongest at LAUNCH */}
        <motion.path
          d="M 340 160 A 145 145 0 0 1 340 240"
          fill="none" stroke="url(#wj-blue)" strokeWidth="8"
          strokeLinecap="round" filter="url(#wj-glow)"
          initial={{ opacity: 0 }}
          animate={{
            opacity: reduce ? 0.9 : [0, 0.05, 0.25, 0.5, 1, 0.5, 0],
          }}
          transition={loopTrans([0, t(4.5), t(6), t(7.5), t(8.4), t(11), 1])}
        />

        {/* Idea seed — a small emerald pulse at the centre for IDEA moment */}
        {!reduce && (
          <motion.circle
            cx="200" cy="200" r="4"
            fill="#3fdca6"
            filter="url(#wj-glow)"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0, 1, 0.4, 0, 0],
              scale:   [0, 0, 1, 3, 6, 6],
            }}
            transition={loopTrans([0, t(1.8), t(2.4), t(3.0), t(3.6), 1])}
            style={{ transformOrigin: "200px 200px" }}
          />
        )}

        {/* Ambient breathing particles — always alive (no cycle).
            Opacity-only animation: cx/cy stay static so React never renders
            undefined SVG attributes during the first framer-motion commit;
            positional drift is already provided by the CSS-orbiting parents. */}
        {!reduce &&
          [
            { cx: 92, cy: 130, r: 1.2, d: 0.4 },
            { cx: 312, cy: 88, r: 1.4, d: 1.1 },
            { cx: 296, cy: 300, r: 1.1, d: 0.7 },
            { cx: 118, cy: 322, r: 1.3, d: 1.4 },
            { cx: 66, cy: 226, r: 1.0, d: 2.0 },
            { cx: 340, cy: 214, r: 1.2, d: 1.7 },
          ].map((p, i) => (
            <motion.circle
              key={i}
              cx={p.cx} cy={p.cy} r={p.r}
              fill="#EFE4CE" opacity="0.55"
              animate={{ opacity: [0.35, 0.7, 0.35] }}
              transition={{
                duration: 8 + p.d * 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: p.d,
              }}
            />
          ))}
      </svg>

      {/* ─── WJ mark + crown + wordmark (STABLE, always visible, gentle breathing) ─── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[2]">
        <div className="relative flex flex-col items-center">
          {/* Crown */}
          <motion.svg
            viewBox="0 0 120 44"
            className="w-[18%] max-w-[110px] mb-[-6px] md:mb-[-8px]"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <g fill="none" stroke="#EFE4CE" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
              <path d="M 8 36 L 16 12 L 34 28 L 60 6 L 86 28 L 104 12 L 112 36 Z" />
              <line x1="8" y1="40" x2="112" y2="40" />
              <circle cx="60" cy="6" r="2.4" fill="#EFE4CE" />
              <circle cx="16" cy="12" r="1.8" fill="#EFE4CE" />
              <circle cx="104" cy="12" r="1.8" fill="#EFE4CE" />
            </g>
          </motion.svg>

          {/* WJ mark — subtle continuous breathing via native CSS */}
          <div
            className="wj-serif-fill wj-mark-breath"
            aria-hidden="true"
          >
            W<span className="wj-serif-j">J</span>
          </div>

          {/* WJ STUDIO wordmark */}
          <motion.div
            className="mt-10 md:mt-14 flex flex-col items-center gap-1.5"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="font-display text-[13px] md:text-[15px] tracking-[0.38em] text-[#EFE4CE]/90 font-light">
              WJ&nbsp;STUDIO
            </span>
            <span className="flex items-center gap-2 text-[9px] md:text-[10px] tracking-[0.36em] text-white/55 font-body uppercase">
              <span>Design</span>
              <span className="w-1 h-1 rounded-full bg-[#28B37A]" />
              <span>Build</span>
              <span className="w-1 h-1 rounded-full bg-[#2B8AF5]" />
              <span>Launch</span>
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
